import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { PageHead } from "@/components/utils";
import { CATEGORIES } from "@/lib/data";
import { IDS } from "@/lib/testIds";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";

export default function Services() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATEGORIES;
    return CATEGORIES.map((c) => ({
      ...c,
      items: c.items.filter((i) => i.name.toLowerCase().includes(q)),
    })).filter((c) => c.items.length > 0);
  }, [query]);

  return (
    <>
      <PageHead
        title="Services & Pricing — Good Looks Unisex Salon"
        description="Full menu and price list for Good Looks Unisex Salon: hair, treatments, facials, hydra, waxing, bleach, makeup and bridal packages."
      />
      <section className="bg-[#F3EAD9] text-[#1B1B1B] pt-40 pb-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow text-[#C89B3C] mb-6">— Services & price list</p>
            <h1 className="font-display text-5xl md:text-8xl leading-[0.95] max-w-5xl">
              Every service.<br /><span className="italic">One clear list.</span>
            </h1>
            <p className="mt-6 text-black/65 max-w-2xl leading-relaxed">
              Search or expand a category to see current pricing. All prices
              start from — final cost depends on hair length, product choice
              and time in the chair.
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="mt-10 max-w-lg relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-black/50" />
              <Input
                data-testid={IDS.servicesSearch}
                type="text"
                placeholder="Search services (e.g. keratin, hydra, bridal)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-11 pr-4 h-14 rounded-full bg-white border border-black/15 focus:border-[#C89B3C]"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#F3EAD9] pb-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <FadeUp>
            <Accordion
              type="multiple"
              defaultValue={filtered.map((c) => c.id)}
              className="w-full border-t border-black/10"
              data-testid={IDS.servicesAccordion}
            >
              {filtered.map((cat, ci) => (
                <AccordionItem
                  key={cat.id}
                  value={cat.id}
                  className="border-b border-black/10"
                  data-testid={`services-cat-${cat.id}`}
                >
                  <AccordionTrigger className="text-left py-8 hover:no-underline">
                    <span className="flex items-baseline gap-6">
                      <span className="text-[#C89B3C] font-display italic text-lg">
                        {String(ci + 1).padStart(2, "0")}
                      </span>
                      <span className="font-display text-2xl md:text-4xl">{cat.title}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10">
                    <ul className="divide-y divide-black/10">
                      {cat.items.map((it, ii) => (
                        <li
                          key={ii}
                          className="flex items-baseline justify-between gap-4 py-4"
                        >
                          <span className="text-black/85 text-sm md:text-base">{it.name}</span>
                          <span className="dots-divider text-black/25 flex-1 mx-4 hidden md:flex" />
                          <span className="font-display italic text-[#C89B3C] text-lg md:text-xl">
                            {it.price}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
              {filtered.length === 0 && (
                <div className="py-16 text-center text-black/50">
                  Nothing matches "{query}". Try another word.
                </div>
              )}
            </Accordion>
          </FadeUp>

          <div className="mt-16 flex flex-wrap gap-4">
            <Link to="/book" className="btn-outline-dark">
              <span>Book a service</span>
              <ArrowUpRight size={16} />
            </Link>
            <Link to="/bridal" className="btn-outline-dark">
              <span>See bridal packages</span>
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
